import {
  createRef,
  FC,
  ReactNode,
  SyntheticEvent,
  useContext,
  useMemo,
} from 'react';

import classNames from 'classnames';

import { useClickEscape, useClickOutside } from '@dtci/triton/hooks';

import Button from '../Button';
import Icon from '../Icon';
import Overlay from '../Overlay';
import {
  BodyPanel,
  FooterPanel,
  HeaderPanel,
  StandardLayout,
} from '../StandardLayout';
import Typography from '../Typography';

import './Modal.scss';

import {
  AnalyticsContext,
  TAnalyticsEventData,
  TContextWithEventData,
  useModalTrack,
} from '@dtci/dash/analytics';

import { MessageType, TestMetadata } from '../types';

export interface ModalProps {
  className?: string;
  /** ID to identify modal */
  modalId?: string;
  /** Set main text for modal header or ARIA label or ReactNode for entire header */
  headerText: string | ReactNode;
  /** Set demoted text for modal header for entire header */
  headerDemotedText?: string;
  /** Boolean to determine if header text should be visible */
  hideHeader?: boolean;
  /** Hide close button */
  hideClose?: boolean;
  /** Type of page overlay: blackout, whiteout */
  bodyOverlay?: 'blackout' | 'whiteout';
  /** Button label for primary action */
  primaryButton?: string;
  /** onChange event for primary action */
  primaryButtonAction?: (e: SyntheticEvent) => void;
  /** Button label for secondary action */
  secondaryButton?: string;
  /** onChange event for secondary action */
  secondaryButtonAction?: (e: SyntheticEvent) => void;
  /** Callback fired when the component requests to be closed */
  onClose?: () => void;
  /** Determine whether to use small buttons */
  smallButtons?: boolean;
  /** Determine where you want to horizontally align the modal: left, center, right */
  horizontalAlign?: 'left' | 'center' | 'right';
  /** Determine where you want to vertically align the modal: top, center, bottom */
  verticalAlign?: 'top' | 'center' | 'bottom';
  /** Determine where you want the header content to be horizontally aligned: space-between, center */
  headerAlign?: 'space-between' | 'center';
  /** Determine where you want your footer content to be horizontally aligned: left, center, right */
  footerAlign?: 'left' | 'center' | 'right' | 'space-between';
  /** Add custom classes to modal's wrapper. */
  modalClass?: string;
  /** Closes modal when you click outside */
  closeOnClickOutside?: boolean;
  /** Determines if you want the modal to close when escape is pressed */
  closeOnEscape?: boolean;
  /** Props to pass to the secondary button */
  secondaryButtonProps?: Record<string, unknown>;
  /** Props to pass to the primary button */
  primaryButtonProps?: Record<string, unknown>;
  /** Dialog custom class name */
  dialogClassName?: string;
  /** Dialog Body custom class name */
  dialogBodyClassName?: string;
  /** Custom footer component */
  footer?: ReactNode;
  // Test IDs for component sections
  testMetadata?: {
    modal?: TestMetadata;
    closeButton?: TestMetadata;
  };
  /** Additional event information to pass to analytics tracking */
  analyticsEventData?: TAnalyticsEventData;
  /** Icon type */
  headerIconType?: MessageType;
}

const Modal: FC<ModalProps> = ({
  modalId,
  headerText,
  headerDemotedText,
  hideHeader,
  hideClose,
  bodyOverlay,
  primaryButton,
  secondaryButton,
  primaryButtonAction,
  secondaryButtonAction,
  horizontalAlign = 'center',
  verticalAlign = 'center',
  headerAlign = 'space-between',
  footerAlign = 'right',
  modalClass,
  onClose,
  children,
  closeOnClickOutside = false,
  closeOnEscape = false,
  smallButtons = false,
  secondaryButtonProps = {},
  primaryButtonProps = {},
  className,
  dialogClassName,
  dialogBodyClassName,
  footer,
  analyticsEventData,
  testMetadata = {
    closeButton: { 'data-testid': 'modalCloseButton' },
  },
  headerIconType,
  ...otherProps
}) => {
  const modalClassNames = useMemo(
    () =>
      classNames(
        'sb-modal',
        className,
        {
          //Horizontal Alignment
          'sb-modal__hor-align-left': horizontalAlign === 'left',
          'sb-modal__hor-align-center': horizontalAlign === 'center',
          'sb-modal__hor-align-right': horizontalAlign === 'right',
          //Vertical Alignment
          'sb-modal__ver-align-top': verticalAlign === 'top',
          'sb-modal__ver-align-center': verticalAlign === 'center',
          'sb-modal__ver-align-bottom': verticalAlign === 'bottom',
        },
        modalClass,
      ),
    [modalClass, horizontalAlign, verticalAlign, className],
  );

  const dialogClassNames = useMemo(
    () => classNames('sb-modal__dialog', dialogClassName),
    [dialogClassName],
  );

  const modalHeaderClassNames = useMemo(
    () =>
      classNames('sb-modal__dialog__header', {
        'sb-modal__dialog__header--align-center': headerAlign === 'center',
      }),
    [headerAlign],
  );

  const modalHeaderIconClassNames = useMemo(
    () =>
      classNames('sb-modal__dialog_header--icon', {
        ['sb-modal__dialog_header--icon-' + headerIconType]: headerIconType,
      }),
    [headerIconType],
  );

  const dialogBodyClassNames = useMemo(
    () =>
      classNames(
        'sb-modal__dialog__body',
        {
          'sb-modal__dialog__body_hide-header': hideHeader && !hideClose,
        },
        dialogBodyClassName,
      ),
    [hideHeader, hideClose, dialogBodyClassName],
  );

  const modalFooterClassNames = useMemo(
    () =>
      classNames('sb-modal__dialog__footer', className, {
        //Footer Alignment
        ['sb-modal__dialog__footer--align-' + footerAlign]: footerAlign,
        'sb-modal__dialog__footer--align-space':
          footerAlign === 'space-between',
      }),
    [footerAlign, className],
  );

  const modalRef = createRef<HTMLDivElement>();

  useClickOutside(
    () => {
      if (closeOnClickOutside && onClose) {
        onClose();
      }
    },
    modalRef,
    [onClose, modalRef],
  );

  useClickEscape(() => {
    if (closeOnEscape && onClose) {
      onClose();
    }
  }, [onClose]);

  const analyticsContext = useContext(AnalyticsContext);

  useModalTrack(
    { analyticsContext, analyticsEventData } as TContextWithEventData,
    {
      headerText,
      className,
      modalId,
    },
  );

  return (
    <div
      {...otherProps}
      className={modalClassNames}
      id={modalId}
      {...testMetadata?.modal}
    >
      <Overlay
        isVisible={bodyOverlay !== undefined}
        type={bodyOverlay === 'whiteout' ? 'light' : 'dark'}
      />
      <div
        className={dialogClassNames}
        role="dialog"
        aria-labelledby={hideHeader ? '' : `${modalId}Header`}
        aria-label={typeof headerText === 'string' ? headerText : 'dialog'}
        ref={modalRef}
      >
        <StandardLayout>
          {!hideHeader && (
            <HeaderPanel
              className={modalHeaderClassNames}
              testMetadata={
                testMetadata.modal
                  ? {
                      'data-testid': `${testMetadata.modal['data-testid']}-header`,
                    }
                  : undefined
              }
            >
              {typeof headerText === 'string' ? (
                <div className="sb-modal-text-container">
                  {headerIconType && (
                    <Icon
                      ariaLabel={headerIconType}
                      className={modalHeaderIconClassNames}
                      faPackage="fas"
                      iconType="fa"
                      size="medium"
                      iconName={
                        headerIconType == 'success'
                          ? 'fa-check-circle'
                          : headerIconType == 'warning'
                          ? 'fa-circle-exclamation'
                          : 'fa-times-circle'
                      }
                    />
                  )}
                  <div className="sb-modal-text-header">
                    <Typography
                      variant="T50"
                      bold
                      id={`${modalId}Header`}
                      className="sb-modal-text-header-label"
                    >
                      {headerText}
                    </Typography>
                    {headerDemotedText && (
                      <Typography
                        variant="T20"
                        id={`${modalId}Demoted`}
                        className="sb-modal-text-demoted-label"
                      >
                        {headerDemotedText}
                      </Typography>
                    )}
                  </div>
                </div>
              ) : (
                typeof headerText === 'object' && headerText
              )}
            </HeaderPanel>
          )}
          {!hideClose && (
            <Button
              sizeType="large"
              styleType="demoted"
              iconOnly
              iconType="fa-times"
              labelText="Close"
              className="sb-modal__close"
              onClick={onClose}
              analyticsEventData={{
                componentName: 'Modal Close Button',
              }}
              {...testMetadata?.closeButton}
            />
          )}
          <BodyPanel className={dialogBodyClassNames}>{children}</BodyPanel>
          {(footer || primaryButton || secondaryButton) && (
            <FooterPanel
              className={modalFooterClassNames}
              data-testid="Modal__Footer"
            >
              {footer
                ? footer
                : (primaryButton || secondaryButton) && (
                    <>
                      {secondaryButton && (
                        <Button
                          sizeType={smallButtons ? 'small' : undefined}
                          styleType="standard"
                          colorType="primary"
                          labelText={secondaryButton}
                          onClick={secondaryButtonAction}
                          className="sb-modal__dialog__secondary-button"
                          analyticsEventData={{
                            componentName: 'Modal Secondary Button',
                          }}
                          {...secondaryButtonProps}
                        >
                          {secondaryButton}
                        </Button>
                      )}
                      {primaryButton && (
                        <Button
                          sizeType={smallButtons ? 'small' : undefined}
                          colorType="primary"
                          labelText={primaryButton}
                          onClick={primaryButtonAction}
                          className="sb-modal__dialog__primary-button"
                          analyticsEventData={{
                            componentName: 'Modal Primary Button',
                          }}
                          {...primaryButtonProps}
                        >
                          {primaryButton}
                        </Button>
                      )}
                    </>
                  )}
            </FooterPanel>
          )}
        </StandardLayout>
      </div>
    </div>
  );
};

export default Modal;
